import React from 'react'
import { useFieldArray } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { t } from 'ttag'

import { updatePath } from 'utils/object'

import { useControlledForm, useFormApiError } from 'hooks'
import { mapInternalStateToPayload } from '../actions'
import {
  QUESTIONS_GROUP_FIELD,
  QUESTION_STATUSES,
  getDefaultValues,
  getValues,
} from '../model'
import { getSchema } from '../schema'

// NOTE: hook that exposes edit form state and its handlers
// for other local hooks and interview templates creator
// components
export const useEditFormState = (state, handlers) => {
  const { activeSection, currentStepIndex, templateUrl, isInsertedCategory } =
    state
  const {
    handleChangeMode,
    handleChangeStep,
    handleDeleteTemplateQuestion,
    handlePatchTemplateQuestion,
    handlePatchTemplateSection,
    handlePostTemplateQuestion,
    handlePostTemplateSection,
    handleSetResponse,
    handleShowSnackbar,
  } = handlers

  const [apiError, setApiError] = React.useState<{
    message?: unknown
    type?: number
  } | null>(null)

  const { id, questions = [], scoreable, title, url } = activeSection

  const defaultValues = getDefaultValues()
  const schema = getSchema()
  const values = getValues({ questions, scoreable, title })

  const handleNewFormData = React.useCallback(
    async data => {
      const payload = mapInternalStateToPayload({ ...data })

      const section = await handlePostTemplateSection({
        interviewTemplate: templateUrl,
        key: payload.title.toLowerCase(),
        order: currentStepIndex,
        scoreable: payload?.scoreable,
        title: payload?.title,
      })

      if (section?.error != null) {
        setApiError({ message: section?.error, type: section?.meta.HTTPstatus })
      } else {
        const questions = await handlePostTemplateQuestion({
          questions: payload.questions.map((question, index) => ({
            ...question,
            order: index + 1,
          })),
          sectionUrl: section?.response?.url,
        })

        handleSetResponse(prevState => {
          handleChangeStep(section?.response?.key)

          return updatePath(
            prevState,
            ['sections', currentStepIndex],
            current => ({
              ...(current as object),
              ...section?.response,
              questions: questions.map(question => question?.response),
            }),
          )
        })
        handleChangeMode()
        handleShowSnackbar({
          message: t`Interview template category was successfully created.`,
          type: 'success',
        })
      }
    },
    [
      currentStepIndex,
      templateUrl,
      handleChangeMode,
      handleChangeStep,
      handlePostTemplateQuestion,
      handlePostTemplateSection,
      handleSetResponse,
      handleShowSnackbar,
    ],
  )

  const handleEditFormData = React.useCallback(
    async data => {
      const payload = mapInternalStateToPayload({ ...data })

      const questions = payload.questions.map(
        (question, index, allQuestions) => {
          const deletedCount = allQuestions
            .slice(0, index)
            .filter(({ status }) => status === QUESTION_STATUSES.DELETED).length

          return {
            ...question,
            order:
              question.status === QUESTION_STATUSES.DELETED
                ? index
                : index + 1 - deletedCount,
          }
        },
      )
      const questionsByStatus: Partial<
        Record<'deleted' | 'fetched' | 'inserted', (typeof questions)[number][]>
      > = {}
      questions.forEach(question => {
        const status =
          question.status === QUESTION_STATUSES.DELETED
            ? 'deleted'
            : question.status === QUESTION_STATUSES.FETCHED
              ? 'fetched'
              : 'inserted'
        questionsByStatus[status] ??= []
        questionsByStatus[status]?.push(question)
      })

      await Promise.all([
        handleDeleteTemplateQuestion({ questions: questionsByStatus.deleted }),
        handlePatchTemplateQuestion({ questions: questionsByStatus.fetched }),
        handlePostTemplateQuestion({
          questions: questionsByStatus.inserted,
          sectionUrl: url,
        }),
      ])

      const section = await handlePatchTemplateSection({
        scoreable: payload?.scoreable,
        title: payload?.title,
        uuid: id,
      })

      if (section?.error != null) {
        setApiError({ message: section?.error, type: section?.meta.HTTPstatus })
      } else {
        handleSetResponse(prevState => {
          handleChangeStep(section?.response?.key)

          return updatePath(
            prevState,
            ['sections', currentStepIndex],
            current => ({
              ...(current as object),
              ...section?.response,
            }),
          )
        })
        handleChangeMode()
        handleShowSnackbar({
          message: t`Interview template category was successfully updated.`,
          type: 'success',
        })
      }
    },
    [
      currentStepIndex,
      id,
      url,
      handleChangeMode,
      handleChangeStep,
      handleDeleteTemplateQuestion,
      handlePatchTemplateQuestion,
      handlePatchTemplateSection,
      handlePostTemplateQuestion,
      handleSetResponse,
      handleShowSnackbar,
    ],
  )

  const handleFormData = React.useCallback(
    async data => {
      isInsertedCategory
        ? await handleNewFormData(data)
        : await handleEditFormData(data)
    },
    [handleEditFormData, handleNewFormData, isInsertedCategory],
  )

  const {
    control,
    formState,
    handleFormApiError,
    handleFormReset,
    handleFormSubmit,
    watch,
  } = useControlledForm({
    values: values ? values : defaultValues,
    resolver: yupResolver(schema),
    handleFormData,
  })

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: QUESTIONS_GROUP_FIELD,
  })

  const hasNoQuestions =
    fields.filter(
      field =>
        (field as unknown as { status?: string }).status !==
        QUESTION_STATUSES.DELETED,
    ).length <= 1

  useFormApiError({ apiError }, handleFormApiError)

  return {
    state: { edit: { control, fields, formState }, hasNoQuestions },
    handlers: {
      handleAppendQuestion: append,
      handleRemoveQuestion: remove,
      handleUpdateQuestion: update,
      handleFormReset,
      handleFormSubmit,
      watch,
    },
  }
}
